import { EFinancingProgram } from '@if/core/types'
import BlockIcon from '@mui/icons-material/Block'
import EmailIcon from '@mui/icons-material/Email'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import PhoneIcon from '@mui/icons-material/Phone'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import TableViewIcon from '@mui/icons-material/TableView'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'
import { alpha, Box, Button, Grid, Typography, useTheme } from '@mui/material'
import { useLaunchFraudIQ } from '@src/data/api/fraud/useFraud'
import { useFeatureFlags } from '@src/data/selectors/app-config-selectors'
import { FraudAnalysisResult, IPQSAnalysis, IpqsEmailDetail, IpqsPhoneDetail } from '@src/data/types/FraudAnalysisResult'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IPQSDetails } from './IPQSDetails'
import { PortalEnrichmentDialog } from './PortalEnrichmentDialog'
import { ScoreGauge } from './ScoreGauge'

interface FraudIQDetailsProps {
  outcomeCode?: string
  decision?: string
  ruleMatchCount?: number
  enquiryMatchCount?: number
  fraudIQApplicationId?: string
}

const FraudIQDetails: React.FC<FraudIQDetailsProps> = ({ outcomeCode, decision, ruleMatchCount, enquiryMatchCount, fraudIQApplicationId }) => {
  const { t } = useTranslation()
  const theme = useTheme()

  if (!outcomeCode && !decision) return null

  const getOutcomeCodeLabel = (code?: string): string => {
    if (!code) return t('fraudFlags.fraudIQ.notAvailable', 'N/A')
    switch (code) {
      case 'APPR':
        return t('fraudFlags.fraudIQ.outcomeAppr', 'Approved')
      case 'REV':
        return t('fraudFlags.fraudIQ.outcomeRev', 'Review')
      case 'DECL':
        return t('fraudFlags.fraudIQ.outcomeDecl', 'Declined')
      default:
        return code
    }
  }

  const getDecisionLabel = (status?: string): string => {
    if (!status) return t('fraudFlags.fraudIQ.notAvailable', 'N/A')
    switch (status) {
      case 'CLEAR':
        return t('fraudFlags.fraudIQ.decisionClear', 'Clear')
      case 'NOTCL':
        return t('fraudFlags.fraudIQ.decisionNotClear', 'Not Clear')
      default:
        return status
    }
  }

  const details = [
    `${t('fraudFlags.fraudIQ.outcomeCode', 'Outcome')}: ${getOutcomeCodeLabel(outcomeCode)}`,
    `${t('fraudFlags.fraudIQ.decision', 'Decision')}: ${getDecisionLabel(decision)}`,
    `${t('fraudFlags.fraudIQ.ruleMatchCount', 'Rule Match Count')}: ${ruleMatchCount ?? 'N/A'}`,
    `${t('fraudFlags.fraudIQ.enquiryMatchCount', 'Enquiry Match Count')}: ${enquiryMatchCount ?? 'N/A'}`,
    ...(fraudIQApplicationId ? [`${t('fraudFlags.fraudIQ.applicationID', 'FraudIQ Application Number')}: ${fraudIQApplicationId}`] : []),
  ]

  return (
    <Box sx={{ mt: 1, width: '100%' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 0.5,
          width: '100%',
        }}
      >
        {details.map((detail, index) => (
          <Box
            key={index}
            sx={{
              px: 1,
              py: 0.5,
              borderRadius: 1,
              backgroundColor: alpha(theme.palette.info.main, 0.1),
              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 28,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.info.dark,
                fontWeight: 500,
                textAlign: 'center',
                fontSize: '0.7rem',
              }}
            >
              {detail}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

interface FraudScoresPanelProps {
  fraudAnalysisResult: FraudAnalysisResult | undefined | null
  ipqsAnalysis: IPQSAnalysis | undefined
  financingProgramId: EFinancingProgram
  creditApplicationId: string
  onBanApplicant?: () => void
  banLabel?: string
  emailDetail?: IpqsEmailDetail | null
  phoneDetail?: IpqsPhoneDetail | null
  ipqsDetailsLoading?: boolean
}

export const FraudScoresPanel: React.FC<FraudScoresPanelProps> = ({ fraudAnalysisResult, ipqsAnalysis, financingProgramId, creditApplicationId, onBanApplicant, banLabel, emailDetail, phoneDetail, ipqsDetailsLoading }) => {
  const { t } = useTranslation()
  const featureFlags = useFeatureFlags()
  const [launchFraudIQ, isLaunching] = useLaunchFraudIQ()
  const [portalDialogOpen, setPortalDialogOpen] = useState(false)
  const fraudIQ = fraudAnalysisResult?.fraudIQAnalysis

  const hideIpqsFraudIq =
    financingProgramId === EFinancingProgram.IfXpress || financingProgramId === EFinancingProgram.MechanicalWarranty

  const canLaunchFraudIQ = useMemo(() => {
    if (!featureFlags.useFraudIQ) return false
    if (fraudIQ?.score === -1) return true
    if (fraudIQ?.fraudIQApplicationId) return false
    return true
  }, [fraudIQ, featureFlags.useFraudIQ])

  const handleLaunchFraudIQ = async () => {
    try {
      await launchFraudIQ({ financingProgramId, creditApplicationId })
    } catch (error) {
      console.error('Failed to launch FraudIQ analysis:', error)
    }
  }

  const getRiskLevel = (score: number): 'LOW' | 'MEDIUM' | 'HIGH' => {
    if (score >= 75) return 'HIGH'
    if (score >= 50) return 'MEDIUM'
    return 'LOW'
  }

  const getFraudIQRiskLevel = (outcomeCode?: string): 'LOW' | 'MEDIUM' | 'HIGH' | undefined => {
    if (!outcomeCode) return undefined
    if (outcomeCode === 'APPR') return 'LOW'
    if (outcomeCode === 'REV') return 'MEDIUM'
    if (outcomeCode === 'DECL') return 'HIGH'
    return undefined
  }

  return (
    <Box sx={{ mb: 3 }}>
      {!hideIpqsFraudIq && <Grid container spacing={3}>
        {/* Fraud IQ Panel */}
        <Grid size={{ xs: 12, md: 4 }}>
          <ScoreGauge
            title={t('fraudFlags.scores.fraudIQ', 'Fraud IQ')}
            score={fraudIQ?.score ?? null}
            maxScore={500}
            unavailable={!fraudIQ?.outcomeCode}
            errorMessage={fraudIQ?.score === -1 ? t('fraudFlags.fraudIQ.errorGettingReport', 'Error while getting FraudIQ report') : undefined}
            riskLevel={fraudIQ?.score === -1 ? undefined : getFraudIQRiskLevel(fraudIQ?.outcomeCode)}
            subtitle={fraudIQ?.analysisDate
              ? new Date(fraudIQ.analysisDate).toLocaleDateString()
              : undefined}
            icon={<VerifiedUserIcon />}
            details={<>
              <FraudIQDetails
                outcomeCode={fraudIQ?.outcomeCode}
                decision={fraudIQ?.decision}
                ruleMatchCount={fraudIQ?.ruleMatchCount}
                enquiryMatchCount={fraudIQ?.enquiryMatchCount}
                fraudIQApplicationId={fraudIQ?.fraudIQApplicationId}
              />
              <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PlayArrowIcon />}
                  loading={isLaunching}
                  loadingPosition="start"
                  onClick={handleLaunchFraudIQ}
                  disabled={!canLaunchFraudIQ}
                >
                  {t('fraudFlags.fraudIQ.launch', 'Launch FraudIQ')}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<OpenInNewIcon />}
                  onClick={() => window.open(import.meta.env.VITE_FRAUDIQ_MANAGER_URL, '_blank')}
                  disabled={!fraudIQ?.fraudIQApplicationId}
                >
                  {t('fraudFlags.fraudIQ.openManager', 'FraudIQ Manager')}
                </Button>
                {featureFlags.useFraudIQPortalScraper && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<TableViewIcon />}
                    onClick={() => setPortalDialogOpen(true)}
                    disabled={!fraudIQ?.fraudIQApplicationId}
                  >
                    {t('fraudFlags.portalEnrichment.viewData', 'Portal Data')}
                  </Button>
                )}
                {fraudIQ?.outcomeCode === 'DECL' && onBanApplicant && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    startIcon={<BlockIcon />}
                    onClick={onBanApplicant}
                  >
                    {banLabel || t('fraudFlags.fraudIQ.banApplicant', 'Ban Applicant')}
                  </Button>
                )}
              </Box>
            </>}
          />
        </Grid>

        {/* IPQS Email Score */}
        <Grid size={{ xs: 12, md: 4 }}>
          <ScoreGauge
            title={t('fraudFlags.scores.ipqsEmail', 'IPQS Email Score')}
            score={ipqsAnalysis?.emailFraudScore ?? null}
            maxScore={100}
            unavailable={!ipqsAnalysis}
            riskLevel={ipqsAnalysis ? getRiskLevel(ipqsAnalysis.emailFraudScore) : undefined}
            subtitle={ipqsAnalysis?.emailAnalysisDate
              ? new Date(ipqsAnalysis.emailAnalysisDate).toLocaleDateString()
              : undefined}
            icon={<EmailIcon />}
            details={<IPQSDetails type="email" analysisMessage={ipqsAnalysis?.emailMessage} emailDetail={emailDetail} loading={ipqsDetailsLoading} />}
          />
        </Grid>

        {/* IPQS Phone Score */}
        <Grid size={{ xs: 12, md: 4 }}>
          <ScoreGauge
            title={t('fraudFlags.scores.ipqsPhone', 'IPQS Phone Score')}
            score={ipqsAnalysis?.phoneFraudScore ?? null}
            maxScore={100}
            unavailable={!ipqsAnalysis}
            riskLevel={ipqsAnalysis ? getRiskLevel(ipqsAnalysis.phoneFraudScore) : undefined}
            subtitle={ipqsAnalysis?.phoneAnalysisDate
              ? new Date(ipqsAnalysis.phoneAnalysisDate).toLocaleDateString()
              : undefined}
            icon={<PhoneIcon />}
            details={<IPQSDetails type="phone" analysisMessage={ipqsAnalysis?.phoneMessage} phoneDetail={phoneDetail} region={ipqsAnalysis?.phoneAssociatedRegion} loading={ipqsDetailsLoading} />}
          />
        </Grid>
      </Grid>}
      {fraudIQ?.fraudIQApplicationId && (
        <PortalEnrichmentDialog
          open={portalDialogOpen}
          onClose={() => setPortalDialogOpen(false)}
          financingProgramId={financingProgramId}
          creditApplicationId={creditApplicationId}
          fraudIQApplicationId={fraudIQ.fraudIQApplicationId}
        />
      )}
    </Box>
  )
}
